/**
 * WILD WEST TRAIN PURSUIT — map pass (2026-11-04, owner: "props more realistic").
 *
 * `propSwaps`: the plaza's box props become real, restyled models (see
 * propSwapLayout.ts for the contract and public/models/props/CREDITS.md for
 * the sources + what we changed):
 *   - the WATER TOWER (tank + roof box → staved tank on a deck under its rafter
 *     roof, "PURGE"/"GULCH" painted on it, girts + X-bracing between the four
 *     collider posts, which keep drawing as timber);
 *   - both COVERED WAGONS (bed + canvas + 4 wheel boxes → painted Conestoga
 *     wagon with our freight line's canvas bonnet);
 *   - the BARRELS (→ branded whiskey casks);
 *   - the plaza WELL (stone base + posts + the flat orange roof board → a
 *     stone well with a plank roof, windlass, rope and bucket);
 *   - the CAMPFIRE (dark ring + flat orange ember box → fieldstone ring,
 *     charred-log teepee, glowing ember bed; built entirely by our pipeline).
 * Colliders are untouched — matched here by size/position, nothing in shared.
 */
import type { MapBox, MapDef } from "@neon-purge/shared";
import { at, sizeIs, wellSwap, type PropSwap } from "../propSwapLayout.js";

export const mapIds = ["wild_west_train_pursuit"];

export function build(): null { return null; }

export function propSwaps(map: MapDef): PropSwap[] {
  const B = map.boxes;
  const out: PropSwap[] = [];
  const used = new Set<MapBox>();
  const take = (sw: PropSwap | null): void => {
    if (!sw || sw.boxes.some((b) => used.has(b))) return;
    for (const b of sw.boxes) used.add(b);
    out.push(sw);
  };
  for (const tank of B) {
    // WATER TOWER: 7 x 4.6 x 7 tank, 8 x 0.8 x 8 roof on it, four 0.5 posts under it
    if (sizeIs(tank, 7, 4.6, 7)) {
      const [x, , z] = tank.p;
      const roof = B.find((b) => sizeIs(b, 8, 0.8, 8) && at(b, x, null, z));
      const legs = B.filter((b) => sizeIs(b, 0.5, 7, 0.5) && Math.abs(Math.abs(b.p[0] - x) - 2.2) < 0.05 && Math.abs(Math.abs(b.p[2] - z) - 2.2) < 0.05);
      if (roof && legs.length === 4) take({ kind: "watertower", boxes: [...legs, tank, roof], replace: [tank, roof] });
    }
    // COVERED WAGON: 3 x 1.4 x 6.4 bed, 3.4 x 1.9 x 5.2 canvas, 4 wheels
    if (sizeIs(tank, 3, 1.4, 6.4)) {
      const [x, , z] = tank.p;
      const canvas = B.find((b) => sizeIs(b, 3.4, 1.9, 5.2) && at(b, x, null, z));
      const wheels = B.filter((b) => sizeIs(b, 0.3, 1.2, 1.2) && Math.abs(Math.abs(b.p[0] - x) - 1.7) < 0.05 && Math.abs(Math.abs(b.p[2] - z) - 2) < 0.05);
      if (canvas && wheels.length === 4) {
        const g = [tank, canvas, ...wheels];
        take({ kind: "wagon", boxes: g, replace: g });
      }
    }
    // BARREL stacks: 1.1 x 1.6 x 1.1 timber
    if (sizeIs(tank, 1.1, 1.6, 1.1) && !tank.tag) take({ kind: "barrel", boxes: [tank], replace: [tank] });
    // the plaza WELL: 3 x 1.1 x 3 stone base
    if (sizeIs(tank, 3, 1.1, 3)) take(wellSwap(B, tank));
    // CAMPFIRE: dark 2.2 x 0.5 x 2.2 ring + a flat orange 1 x 0.7 x 1 "ember" box
    if (sizeIs(tank, 2.2, 0.5, 2.2)) {
      const embers = B.find((b) => sizeIs(b, 1, 0.7, 1) && at(b, tank.p[0], null, tank.p[2]));
      if (embers) take({ kind: "campfire", boxes: [tank, embers], replace: [tank, embers] });
    }
  }
  return out;
}
